// Registers the service worker and, just as importantly, makes sure a newly
// deployed one actually takes over.
//
// The stock CRA version of this file registered the worker and stopped there.
// A new worker installed, went to `waiting`, and stayed there until every
// client for the origin was closed - which inside a WebView is "until the user
// force-stops or reinstalls the app". Meanwhile the old worker kept serving the
// old shell, whose chunk filenames the deploy had already replaced. See the
// comment at the top of src/service-worker.js.

import { hardReset } from './utils/hardReset';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// How often a long-lived client re-checks for a new worker. A WebView session
// can stay open for days without a single navigation, which is exactly the
// client most likely to go stale.
const UPDATE_INTERVAL_MS = 30 * 60 * 1000;

const RELOADED_FLAG = 'deskos:sw-controller-reload';

const hadControllerAtStartup = Boolean(
  typeof navigator !== 'undefined' && navigator.serviceWorker && navigator.serviceWorker.controller
);

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    listenForStaleBuild();

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

// The worker tells us when a hashed chunk 404s, which only happens when this
// client is running a build the server has replaced. Recover rather than wait
// for the render to throw.
function listenForStaleBuild() {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'DESKOS_STALE_BUILD') {
      hardReset();
    }
  });

  // A new worker calling skipWaiting() + clientsClaim() takes over this client
  // while it is still running the previous build's JavaScript. One reload puts
  // the document on the build the new worker is serving. Guarded so a worker
  // that keeps changing cannot spin the page.
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;

    // No controller before this means a first install, not an update. Nothing
    // is stale, so leave the page alone.
    if (!hadControllerAtStartup) return;

    let alreadyReloaded = null;
    try {
      alreadyReloaded = window.sessionStorage.getItem(RELOADED_FLAG);
    } catch (ignored) {
      alreadyReloaded = null;
    }
    if (alreadyReloaded) return;

    try {
      window.sessionStorage.setItem(RELOADED_FLAG, '1');
    } catch (ignored) {
      // Reload anyway.
    }

    reloading = true;
    window.location.reload();
  });
}

function scheduleUpdateChecks(registration) {
  const check = () => {
    registration.update().catch(() => {});
  };

  setInterval(check, UPDATE_INTERVAL_MS);

  // Coming back from the background is the moment a WebView is most likely to
  // be on a build older than the server's.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    // `updateViaCache: 'none'` stops the browser answering the update check for
    // service-worker.js out of its own HTTP cache. Without it a WebView holding
    // the script for up to 24 hours never even learns a new worker exists.
    .register(swUrl, { updateViaCache: 'none' })
    .then((registration) => {
      scheduleUpdateChecks(registration);

      // Anything already parked in `waiting` is from before skipWaiting() was
      // added to the worker. Push it through.
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // The worker skips waiting itself; this is belt and braces for a
              // client whose old worker predates that change.
              installingWorker.postMessage({ type: 'SKIP_WAITING' });

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else if (config && config.onSuccess) {
              config.onSuccess(registration);
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (response.status === 404 || (contentType != null && contentType.indexOf('javascript') === -1)) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
